'use client';
import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { forgeFoodService } from '@/lib/services/forgeFoodService';
import { createClient } from '@/lib/supabase/client';

interface FoodEntry {
  id: string;
  name: string;
  grams: number;
  cal: number;
  protein: number;
  carbs: number;
  fat: number;
}

interface Meal {
  id: string;
  name: string;
  entries: FoodEntry[];
}

export interface MealSectionsHandle {
  addFoodToMeal: (food: Omit<FoodEntry, 'id'>, mealId: string) => void;
  getMeals: () => { id: string; name: string }[];
}

const MEAL_DEFS: { id: string; name: string }[] = [
  { id: 'meal-breakfast', name: 'Breakfast' },
  { id: 'meal-lunch', name: 'Lunch' },
  { id: 'meal-dinner', name: 'Dinner' },
  { id: 'meal-snacks', name: 'Snacks' },
  { id: 'meal-pre', name: 'Pre-Workout' },
  { id: 'meal-post', name: 'Post-Workout' },
];

function mealTotal(entries: FoodEntry[]) {
  return entries.reduce(
    (acc, e) => ({ cal: acc.cal + e.cal, protein: acc.protein + e.protein, carbs: acc.carbs + e.carbs, fat: acc.fat + e.fat }),
    { cal: 0, protein: 0, carbs: 0, fat: 0 }
  );
}

const mealTypeMap: Record<string, string> = {
  'meal-breakfast': 'breakfast', 'meal-lunch': 'lunch', 'meal-dinner': 'dinner',
  'meal-snacks': 'snacks', 'meal-pre': 'pre_workout', 'meal-post': 'post_workout',
};

const MealSections = forwardRef<MealSectionsHandle>(function MealSections(_props, ref) {
  const [meals, setMeals] = useState<Meal[]>(
    MEAL_DEFS.map((m) => ({ ...m, entries: [] }))
  );
  const [expandedMeals, setExpandedMeals] = useState<Set<string>>(
    new Set(['meal-breakfast', 'meal-lunch', 'meal-dinner', 'meal-snacks'])
  );
  const [flashEntry, setFlashEntry] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) setUserId(user.id);
    });

    // Load today's log from Supabase
    forgeFoodService.getTodayLog().then((entries) => {
      if (!entries || entries.length === 0) return;
      setMeals((prev) =>
        prev.map((meal) => {
          const dbMealType = mealTypeMap[meal.id] || meal.id.replace('meal-', '');
          const mealEntries = entries
            .filter((e) => e.mealType === dbMealType)
            .map((e) => ({
              id: e.id,
              name: e.foodName,
              grams: e.grams,
              cal: e.cal,
              protein: e.proteinG,
              carbs: e.carbsG,
              fat: e.fatG,
            }));
          return { ...meal, entries: mealEntries };
        })
      );
    });
  }, []);

  useImperativeHandle(ref, () => ({
    addFoodToMeal(food: Omit<FoodEntry, 'id'>, mealId: string) {
      const newId = `entry-${Date.now()}`;
      setMeals((prev) =>
        prev.map((m) =>
          m.id === mealId ? { ...m, entries: [...m.entries, { ...food, id: newId }] } : m
        )
      );
      setExpandedMeals((prev) => new Set([...prev, mealId]));
      setFlashEntry(newId);
      setTimeout(() => setFlashEntry(null), 2000);
    },
    getMeals() {
      return meals.map((m) => ({ id: m.id, name: m.name }));
    },
  }));

  function toggleMeal(id: string) {
    setExpandedMeals((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function removeEntry(mealId: string, entryId: string) {
    setMeals((prev) =>
      prev.map((m) => m.id === mealId ? { ...m, entries: m.entries.filter((e) => e.id !== entryId) } : m)
    );
    try {
      await forgeFoodService.deleteLogEntry(entryId);
    } catch {
      // non-blocking
    }
  }

  const totalCal = meals.reduce((sum, m) => sum + mealTotal(m.entries).cal, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-forge-label">Today&apos;s Meals</div>
        {totalCal > 0 && (
          <div className="text-xs font-tabular font-bold text-foreground">
            Total: <span className="text-primary">{totalCal}</span> kcal
          </div>
        )}
      </div>
      {meals.map((meal) => {
        const totals = mealTotal(meal.entries);
        const isExpanded = expandedMeals.has(meal.id);
        return (
          <div key={meal.id} className="card-forge rounded-xl overflow-hidden">
            <button
              onClick={() => toggleMeal(meal.id)}
              className="w-full flex items-center justify-between p-4 hover:bg-card-elevated transition-colors"
            >
              <div className="flex items-center gap-3">
                <Icon name={isExpanded ? 'ChevronDownIcon' : 'ChevronRightIcon'} size={16} className="text-muted-foreground" />
                <div className="text-left">
                  <div className="font-bold text-sm text-foreground tracking-wider uppercase">{meal.name}</div>
                  {meal.entries.length > 0 && (
                    <div className="text-2xs text-muted-foreground">{meal.entries.length} item{meal.entries.length !== 1 ? 's' : ''}</div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs font-tabular">
                {totals.cal > 0 && (
                  <>
                    <div className="text-right hidden sm:block">
                      <span className="text-muted-foreground">P </span>
                      <span className="font-bold text-foreground">{totals.protein}g</span>
                    </div>
                    <div className="text-right hidden sm:block">
                      <span className="text-muted-foreground">C </span>
                      <span className="font-bold text-foreground">{totals.carbs}g</span>
                    </div>
                    <div className="font-black text-foreground">{totals.cal} kcal</div>
                  </>
                )}
                {totals.cal === 0 && (
                  <span className="text-xs text-muted-foreground">Empty</span>
                )}
              </div>
            </button>

            {isExpanded && (
              <div className="border-t border-border">
                {meal.entries.length === 0 ? (
                  <div className="p-6 text-center text-muted-foreground text-sm">
                    <div className="text-2xl mb-2">🍽️</div>
                    No foods logged for {meal.name.toLowerCase()} yet.
                    <div className="text-xs mt-1 text-muted-foreground/60">Use AI Search or Hunter&apos;s List to add food</div>
                  </div>
                ) : (
                  meal.entries.map((entry) => (
                    <div
                      key={entry.id}
                      className={`group flex items-center justify-between px-4 py-3 hover:bg-card-elevated transition-all border-b border-border/50 last:border-0 ${
                        flashEntry === entry.id ? 'bg-primary/10 border-primary/30' : ''
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-foreground truncate">{entry.name}</div>
                          {flashEntry === entry.id && (
                            <span className="text-2xs px-1.5 py-0.5 rounded bg-primary/20 text-primary font-bold flex-shrink-0">NEW</span>
                          )}
                        </div>
                        <div className="text-2xs text-muted-foreground">{entry.grams}g</div>
                      </div>
                      <div className="flex items-center gap-3 ml-4">
                        <div className="hidden sm:flex items-center gap-3 text-xs font-tabular text-muted-foreground">
                          <span><span className="text-primary">P</span> {entry.protein}g</span>
                          <span><span className="text-accent">C</span> {entry.carbs}g</span>
                          <span><span className="text-muted-foreground">F</span> {entry.fat}g</span>
                        </div>
                        <span className="font-tabular font-bold text-sm text-foreground w-16 text-right">{entry.cal} kcal</span>
                        <button
                          onClick={() => removeEntry(meal.id, entry.id)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                        >
                          <Icon name="TrashIcon" size={14} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
});

export default MealSections;
